/**
 * Date Utilities
 *
 * Shared date conversion and manipulation functions.
 * Single source of truth for date handling across the app.
 */

/**
 * Safely convert various date formats to Date object
 * Handles: Firestore Timestamp, Date, string, number (Unix timestamp)
 *
 * @param val - Value to convert (Timestamp, Date, string, number, null, undefined)
 * @param fallback - Fallback value if conversion fails (default: new Date())
 * @returns Date object
 */
export function toDateSafe(val: unknown, fallback?: Date): Date {
  if (!val) {
    return fallback ?? new Date();
  }

  // Firestore Timestamp (has toDate method)
  if (typeof val === 'object' && val !== null && 'toDate' in val && typeof (val as any).toDate === 'function') {
    return (val as any).toDate();
  }

  // Already a Date
  if (val instanceof Date) {
    return val;
  }

  // String or Unix timestamp
  if (typeof val === 'string' || typeof val === 'number') {
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return fallback ?? new Date();
}

/**
 * Convert Date to Firestore-safe format
 * Returns undefined if the input is falsy (Firestore doesn't accept undefined, caller should handle)
 */
export function toDateOrUndefined(val: unknown): Date | undefined {
  if (!val) {
    return undefined;
  }
  return toDateSafe(val);
}

/**
 * Get ISO date string (YYYY-MM-DD) for a date
 */
export function toISODateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Get start of day for a date
 */
export function startOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Get end of day for a date
 */
export function endOfDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Get start of current week (Monday)
 */
export function startOfWeek(date: Date): Date {
  const result = new Date(date);
  const day = result.getDay();
  const daysFromMonday = day === 0 ? 6 : day - 1;
  result.setDate(result.getDate() - daysFromMonday);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Calculate days between two dates
 */
export function daysBetween(date1: Date, date2: Date): number {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
}

/**
 * Day of the current week, Monday = 1 … Sunday = 7.
 */
export function dayOfWeekMon1(now: Date = new Date()): number {
  return ((now.getDay() + 6) % 7) + 1;
}

/**
 * Is the current calendar week far enough along for a week-to-date delta to
 * be worth showing?
 *
 * MECHANISM (checked against unifiedIntelligence.calculateTrends, not
 * assumed): `weekToDateDelta` compares Monday→now against Monday→the SAME
 * WEEKDAY last week. So it is not partial-week-vs-full-week — it is
 * partial-DAY vs full-day. Today is only partly over; the day it is measured
 * against is complete.
 *
 * That skew is small mid-week, when several days of totals have accumulated
 * and one pending session is a minor share. Early in the week it dominates:
 * on a Monday morning a perfectly consistent athlete has a cumulative total
 * of zero against a complete previous Monday, and every tile turns red —
 * `SESSIONS 0 (-1)`, `VOLUME 0 (-11685)`, `FREQUENCY 0/wk (-1)`. They have
 * not collapsed; they have not trained YET today. Rendering that as a
 * regression teaches people to ignore the indicator.
 *
 * Wednesday (day 3 of 7) is where enough cumulative total exists for a single
 * not-yet-completed session to stop dominating the comparison.
 *
 * NOTE this narrows the artefact rather than eliminating it — the
 * partial-day skew still exists Wed–Sun, just muted. The structural fix is a
 * TRAILING 7-DAY window, which has no day boundary to be early in. That is a
 * larger change to the trends pipeline and is deliberately not done here.
 *
 * Callers should render `WEEK_IN_PROGRESS_LABEL` instead of a delta when this
 * returns false.
 */
export function isWeekComparable(now: Date = new Date()): boolean {
  return dayOfWeekMon1(now) >= 3;
}

/** Copy shown in place of a delta while the week is still too young to judge. */
export const WEEK_IN_PROGRESS_LABEL = 'week in progress';
export const WEEK_IN_PROGRESS_TITLE =
  'Week still in progress — too early to compare against a full week';
